import { useEffect, useState } from "react";
import {
  getScheduleUpdates,
  markScheduleUpdateRead,
} from "../../../../services/schedule-updates";
import type { ScheduleUpdateNotification } from "../../../../types/schedule-updates";
import { describeScheduleUpdate, hasChanges } from "./scheduleUpdateSummary";

interface ScheduleUpdateBannerProps {
  /** The tournament event whose unread update (if any) this banner surfaces. */
  calendarEventId: string;
}

/**
 * "What changed" banner for a tournament event. Fetches the caller's unread
 * schedule updates, finds the one for this event, and shows a neutral,
 * informational summary of the counts.
 *
 * Mark-read UX: we mark the notification read as soon as the banner is shown
 * for this event (the plan's "mark-read on view"). Viewing the event is what
 * "seeing the update" means, and it keeps seen-state server-authoritative and
 * consistent across devices. The dismiss button then just hides the banner
 * locally; the server already knows it was seen, so it never reflashes on a
 * remount or on another device.
 */
export const ScheduleUpdateBanner = ({ calendarEventId }: ScheduleUpdateBannerProps) => {
  const [notification, setNotification] = useState<ScheduleUpdateNotification | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Reset visible state when the event changes so a stale banner never shows
    // against a different event.
    setNotification(null);
    setDismissed(false);

    // Guard against the set-state-after-await race: if the event changes (or the
    // panel unmounts) mid-flight, we drop the resolved result instead of writing
    // it into a now-stale render.
    let active = true;
    const controller = new AbortController();

    getScheduleUpdates(true, controller.signal)
      .then((updates) => {
        if (!active) return;
        const match = updates.find(
          (u) => u.calendarEventId === calendarEventId && hasChanges(u)
        );
        if (!match) return;
        setNotification(match);
        // Mark-read on view (fire and forget). A failure just leaves it unread
        // server-side; it does not break the banner the user is looking at.
        markScheduleUpdateRead(match.id).catch(() => {
          /* non-fatal: stays unread, will resurface next visit */
        });
      })
      .catch(() => {
        /* non-fatal: no banner if we can't load updates */
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [calendarEventId]);

  if (!notification || dismissed) return null;

  const message = describeScheduleUpdate(notification);
  if (!message) return null;

  return (
    <div className="schedule-update-banner" role="status" data-testid="schedule-update-banner">
      <span className="schedule-update-banner__icon" aria-hidden="true">
        🔄
      </span>
      <span className="schedule-update-banner__msg">{message}</span>
      <button
        type="button"
        className="schedule-update-banner__dismiss"
        onClick={() => setDismissed(true)}
        aria-label="Dismiss update"
        data-testid="schedule-update-banner-dismiss"
      >
        ×
      </button>
    </div>
  );
};
